import { ApiError } from '../../lib/api';
import { getIdToken } from '../../lib/firebase';
import type { QrStyle, StyleIssue } from './types';

/**
 * Llamadas de estilo que no son JSON→JSON (PNG de vista previa y subida multipart del logo).
 * `lib/api` solo maneja JSON, así que se replican aquí sus cabeceras (token, organización, correlación).
 */
function baseUrl(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
}

async function headers(orgId?: string): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'X-Correlation-Id': globalThis.crypto?.randomUUID?.() ?? `cid-${Date.now()}` };
  const token = await getIdToken();
  if (token) h.Authorization = `Bearer ${token}`;
  if (orgId) h['X-Org-Id'] = orgId;
  return h;
}

async function toError(res: Response): Promise<ApiError> {
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    /* sin cuerpo JSON */
  }
  const code = typeof body.code === 'string' ? body.code : undefined;
  const status = res.status;
  const kind =
    status === 401 ? 'unauthorized' : status === 403 ? 'forbidden' : status === 404 ? 'not_found' : status === 409 ? 'conflict' : status === 422 || status === 400 || status === 413 || status === 415 ? 'validation' : status === 429 ? 'rate_limited' : 'server';
  return new ApiError({
    kind,
    status,
    code,
    message: typeof body.error === 'string' ? body.error : `Error ${status}`,
    retryAfterSeconds: res.headers.get('Retry-After') ? Number(res.headers.get('Retry-After')) : undefined,
    details: body.details,
  });
}

export interface PreviewResult {
  blob: Blob;
  issues: StyleIssue[];
}

export async function fetchStylePreview(qrStyle: QrStyle, data: string | undefined, orgId: string | undefined, signal?: AbortSignal): Promise<PreviewResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}/v1/qr-styles/preview`, {
      method: 'POST',
      headers: { ...(await headers(orgId)), 'Content-Type': 'application/json', Accept: 'image/png' },
      body: JSON.stringify({ qrStyle: stripLogoUrl(qrStyle), data }),
      signal,
    });
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err;
    throw new ApiError({ kind: 'network', status: 0, message: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.' });
  }
  if (!res.ok) throw await toError(res);
  let issues: StyleIssue[] = [];
  const raw = res.headers.get('X-Style-Issues');
  if (raw) {
    try {
      issues = JSON.parse(raw) as StyleIssue[];
    } catch {
      issues = [];
    }
  }
  return { blob: await res.blob(), issues };
}

export async function uploadLogo(file: File, orgId: string | undefined): Promise<{ logoId: string; url?: string }> {
  const form = new FormData();
  form.append('file', file);
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}/v1/logos`, { method: 'POST', headers: await headers(orgId), body: form });
  } catch {
    throw new ApiError({ kind: 'network', status: 0, message: 'No pudimos conectarnos. Revisa tu conexión e intenta de nuevo.' });
  }
  if (!res.ok) throw await toError(res);
  return (await res.json()) as { logoId: string; url?: string };
}

/** La URL firmada del logo es solo para mostrarlo en la SPA; la API no la acepta dentro de `qrStyle`. */
export function stripLogoUrl(style: QrStyle): QrStyle {
  if (!style.logo) return style;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { url, ...logo } = style.logo;
  return { ...style, logo };
}
